import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";
import { apiLongRunningRequestTimeoutMs } from "../config";
import {
  IIntelligencePricingReviewResponse,
  IListingOptimisationPartsRequest,
  IPartIntelligenceReviewResponse,
} from "../models/listingOptimisation/IListingReview";

/**
 * Intelligent pricing with AI review for the eBay Optimisation queue. The bulk call runs the
 * marketplace lookup per part, has the AI keep only the listings that are the same part, and
 * stages the kept average as the new price - or holds the part with a reason when the figures
 * are not good enough to price on. Nothing is sent to eBay until the listing is relisted.
 *
 * `latestReview` is the stored result of the last lookup for one part, for the drawer's
 * "7 kept of 11 found" line and the "See the 11" list.
 */
export default function useIntelligentPricingReview(
  partId?: string,
  componentName = "useIntelligentPricingReview"
) {
  const { get } = useApiHelper(apiConfig(componentName));
  // The lookup hits the live marketplace per part and then the AI, so it regularly runs past
  // the default JSON timeout.
  const { post: postLongRunning } = useApiHelper({
    ...apiConfig(componentName),
    timeoutMs: apiLongRunningRequestTimeoutMs,
  });
  const queryClient = useQueryClient();

  const priceFromIntelligence = useMutation(
    (partDataIds: string[]) =>
      postLongRunning<IListingOptimisationPartsRequest, IIntelligencePricingReviewResponse>(
        "/listingoptimisation/price-from-intelligence",
        { partDataIds }
      ),
    {
      onSuccess: (_, partDataIds) => {
        queryClient.invalidateQueries(["listingOptimisations"]);
        queryClient.invalidateQueries(["pagedTable"]);
        partDataIds.forEach((id) => {
          queryClient.invalidateQueries(["intelligenceReview", id]);
          queryClient.invalidateQueries(["partDetails", id]);
        });
      },
    }
  );

  const latestReview = useQuery({
    queryKey: ["intelligenceReview", partId],
    queryFn: () =>
      get<IPartIntelligenceReviewResponse>(`/listingoptimisation/intelligence-review/${partId}`),
    enabled: !!partId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  return {
    priceFromIntelligence,
    latestReview,
  };
}
